/* eslint-disable no-undef */
import "../assets/css/ProfilePage.css";
import React, { useState, useEffect, useRef, createRef } from "react";
import { useNavigate } from "react-router-dom";
import { TopBarMenu } from "../components/TopBarMenu";
import { TopLeftComponent } from "../components/TopLeftComponent";
import {
  useGetUserByIdQuery,
  usePatchUserMutation,
  useUpdateBackgroundImageMutation,
  useUpdateProfileImageMutation,
  useDeleteAccountMutation,
} from "../slices/UserSlice";
import { useSelector, useDispatch } from "react-redux";
import { LoadingProfilePage } from "../components/LoadingProfilePage";
import { EditProfileSocialsComponent } from "../components/EditProfileSocialsComponent";
import ReactQuill, { displayName } from "react-quill";
import "react-quill/dist/quill.snow.css";
import { UserNameInputComponent } from "../components/UserNameInputComponent";
import { UserTitleInputComponent } from "../components/UserTitleInputComponent";
import { IoRemoveCircleOutline, IoCameraReverseOutline } from "react-icons/io5";
import { updateUserName, updateAsLoggedOut } from "../slices/UserSlice";
import { parrotBlue, parrotRed, parrotTextDarkBlue } from "../styles/colors";
import { SomethingWentWrong } from "../components/SomethingWentWrong";
import { useHealthCheckQuery } from "../slices/HealthSlice";
import { toast } from "react-toastify";

/* TODO:
when pressed update:
  1. handle upload profile image
  2. handle upload background image
  3. handle patch user
  4. update state changes to local storage
*/

export function EditProfilePage() {
  const local_userId = localStorage.getItem("storedUserId");
  const state_userId = useSelector((state) => state.users.userId);
  const userId = local_userId !== null ? local_userId : state_userId;
  const isDarkMode = useSelector((state) => state.users.isDarkMode);
  const dark = isDarkMode;
  const dispatch = useDispatch();
  const fileInputRef_ProfileImage = createRef();
  const fileInputRef_BackgroundImage = createRef();
  const [backGroundImage, setBackGroundImage] = useState(null);
  const [backGroundImagePreview, setBackGroundImagePreview] = useState(null);
  const [profileImage, setProfileImage] = useState(null);
  const [profileImagePreview, setProfileImagePreview] = useState(null);
  const [userName, setUserName] = useState("");
  const [userTitle, setUserTitle] = useState("");
  const [userBio, setUserBio] = useState("");
  const [email, setEmail] = useState("");
  const [displayEmail, setDisplayEmail] = useState("");
  const [phoneNumber, setPhoneNumber] = useState("");
  const [facebookProfile, setFacebookProfile] = useState("");
  const [instagramProfile, setInstagramProfile] = useState("");
  const [twitterProfile, setTwitterProfile] = useState("");
  const [tiktokProfile, setTiktokProfile] = useState("");
  const [linkedinProfile, setLinkedinProfile] = useState("");
  const [youtubeProfile, setYoutubeProfile] = useState("");
  const [emailHidden, setEmailHidden] = useState();
  const [honeyPotValue, setHoneyPotValue] = useState("");
  const [isUpdatingProfile, setIsUpdatingProfile] = useState(false);
  const navigate = useNavigate();
  const apiUrl = process.env.REACT_APP_API_URL;
  const userBaseUrl = ``;
  const handleGoToPublicProfilePage = () => {
    navigate(`/profile-public/${userData.publicId}/${userData.userName}`);
  };
  const handleGoToProfilePage = () => {
    navigate(`/profile`);
  };

  const [patchUser] = usePatchUserMutation();
  const [updateBackgroundImage] = useUpdateBackgroundImageMutation();
  const [updateProfileImage] = useUpdateProfileImageMutation();
  const [deleteAccount] = useDeleteAccountMutation();
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [isDeletingAccount, setIsDeletingAccount] = useState(false);

  const handleDeleteAccount = async () => {
    setIsDeletingAccount(true);
    try {
      await deleteAccount().unwrap();
    } catch (_) { }
    setIsDeletingAccount(false);
    setShowDeleteModal(false);
    dispatch(updateAsLoggedOut());
  };

  const {
    data: userData,
    isLoading: isLoadingUser,
    isError: isErrorUser,
    error,
    isSuccess: isSuccessUser,
    refetch: refetchUserData,
  } = useGetUserByIdQuery(userId);

  useEffect(() => {
    if (isSuccessUser) {
      setUserName(userData.userName);
      setUserTitle(userData.title);
      setUserBio(userData.bio);
      setProfileImage(userData.profileImageUrl);
      setBackGroundImage(userData.backgroundImageUrl);
      setEmail(userData.email || "");
      setDisplayEmail(userData.displayEmail || "");
      setInstagramProfile(userData.instagram || "");
      setYoutubeProfile(userData.youtube || "");
      setFacebookProfile(userData.facebook || "");
      setPhoneNumber(userData.phoneNumber || "");
      setTwitterProfile(userData.twitter || "");
      setLinkedinProfile(userData.linkedin || "");
      setTiktokProfile(userData.tiktok || "");
      setEmailHidden(!userData.emailVisible);
    }
  }, [userData, isSuccessUser]);

  const handlePatchUser = async () => {
    const patchDoc = [
      { op: "replace", path: "/userName", value: userName },
      // { op: "replace", path: "/email", value: email },
      { op: "replace", path: "/displayEmail", value: displayEmail },
      { op: "replace", path: "/phonenumber", value: phoneNumber },
      { op: "replace", path: "/facebook", value: facebookProfile },
      { op: "replace", path: "/instagram", value: instagramProfile },
      { op: "replace", path: "/twitter", value: twitterProfile },
      { op: "replace", path: "/tiktok", value: tiktokProfile },
      { op: "replace", path: "/linkedin", value: linkedinProfile },
      { op: "replace", path: "/youtube", value: youtubeProfile },
      { op: "replace", path: "/title", value: userTitle },
      { op: "replace", path: "/bio", value: userBio },
      { op: "replace", path: "/emailVisible", value: !emailHidden },
    ];
    try {
      await patchUser({ patchDoc, userId }).unwrap();
      dispatch(updateUserName({ username: userName }));
    } catch (error) {
      console.error("Error updating profile", error);
      toast.error("Failed to update profile. Please try again.");
    }
  };

  const handleBackGroundImageChange = (e) => {
    const files = e.target.files;
    if (files && files.length > 0) {
      const file = files[0];

      const maxSizeMB = 5;
      const maxSizeBytes = maxSizeMB * 1024 * 1024;
      if (file.size > maxSizeBytes) {
        toast.error("File size must be 5MB or less.");
        return;
      }

      setBackGroundImage(file);
      const previewUrl = URL.createObjectURL(file);
      setBackGroundImagePreview(previewUrl);
    }
  };

  const handleProfileImageChange = (e) => {
    const files = e.target.files;
    if (files && files.length > 0) {
      const file = files[0];

      const maxSizeMB = 5;
      const maxSizeBytes = maxSizeMB * 1024 * 1024;
      if (file.size > maxSizeBytes) {
        toast.error("File size must be 5MB or less.");
        return;
      }

      setProfileImage(file);
      const previewUrl = URL.createObjectURL(file);
      setProfileImagePreview(previewUrl);
    }
  };

  const handleBackGroundImageClick = () => {
    fileInputRef_BackgroundImage.current.click();
  };

  const handleProfileImageClick = () => {
    fileInputRef_ProfileImage.current.click();
  };

  const handleCancelUploadBackGroundImage = () => {
    backGroundImagePreview && URL.revokeObjectURL(backGroundImagePreview);
    setBackGroundImage(userData.backgroundImageUrl);
    setBackGroundImagePreview(null);
    if (fileInputRef_BackgroundImage.current) {
      fileInputRef_BackgroundImage.current.value = "";
    }
  };

  const handleCancelUploadProfileImage = () => {
    profileImagePreview && URL.revokeObjectURL(profileImagePreview);
    setProfileImage(userData.profileImageUrl);
    setProfileImagePreview(null);
    if (fileInputRef_ProfileImage.current) {
      fileInputRef_ProfileImage.current.value = "";
    }
  };

  const handleUploadProfileImage = async () => {
    if (!profileImage || profileImage === userData.profileImageUrl) {
      console.log("---- not doing anything with profileImage");
      return;
    }
    try {
      await updateProfileImage({ profileImage, userId }).unwrap();
    } catch (error) {
      console.error("Error uploading profile image", error);
      toast.error("Failed to update profile image. Please try again.");
    }
  };

  const handleUploadBackgroundImage = async () => {
    if (!backGroundImage || backGroundImage === userData.backgroundImageUrl) {
      console.log("---- not doing anything with profileImage");

      return;
    }
    try {
      await updateBackgroundImage({ backGroundImage, userId }).unwrap();
    } catch (error) {
      console.error("Error uploading background image", error);
      toast.error("Failed to update background image. Please try again.");
    }
  };

  const handleUpdateChanges = async () => {

    if (honeyPotValue) {
      console.warn("Bot detected – update blocked");
      return;
    }

    setIsUpdatingProfile(true);
    if (backGroundImage) {
      await handleUploadBackgroundImage();
    }
    if (profileImage) {
      await handleUploadProfileImage();
    }
    await handlePatchUser();
    await refetchUserData();
    setIsUpdatingProfile(false);
  };

  const { data: healthCheckData, isError: isHealthCheckError } =
    useHealthCheckQuery();

  if (isHealthCheckError) {
    console.log(".....Health check failed.....");
    return <SomethingWentWrong />;
  }

  if (isErrorUser) {
    return <SomethingWentWrong />;
  }

  return isLoadingUser ? (
    <LoadingProfilePage />
  ) : isSuccessUser ? (
    <div className="App">
      <header className="App-header">
        <div className="flex mainpage_Container">
          <div className="flex mainpage_TopRow">
            <TopLeftComponent />
            <div className="flex mainpage_TopRight">
              <TopBarMenu />
            </div>
          </div>

          <div className="flex profilePage_Bottom">
            <div className="flex profilePage_BottomLeft">
              <div className="flex profilePage_CoverAndProfile">

                <div style={buttonsContainer}>
                  <div
                    style={{
                      display: "flex",
                      flexDirection: "row",
                      gap: "0.5rem",
                    }}
                  >
                    <div onClick={() => handleGoToProfilePage()} style={navigationButton}>
                      <span>Profile</span>
                    </div>


                    {local_userId === userId &&
                      <div onClick={() => handleGoToPublicProfilePage()} style={navigationButton}>
                        <span>Public Profile</span>
                      </div>
                    }
                  </div>
                </div>


                <div className="flex profilePage_CoverImage">
                  <input
                    type="file"
                    accept="image/*"
                    onChange={handleBackGroundImageChange}
                    style={{ display: "none" }}
                    ref={fileInputRef_BackgroundImage}
                  />
                  {backGroundImagePreview ? (
                    <div
                      className="
                            // image-preview 
                            profilePage_CoverImage_Img"
                    >
                      <img
                        src={backGroundImagePreview}
                        alt=""
                        style={{
                          width: "100%",
                          height: "100%",
                          objectFit: "cover",
                          border: "2px solid transparent",
                          overFlow: "hidden",
                          borderRadius: "1rem",
                        }}
                      />
                    </div>
                  ) : (
                    <img
                      src={userBaseUrl + backGroundImage}
                      alt="Upload Icon"
                      onClick={handleBackGroundImageClick}
                      style={{
                        width: "100%",
                        height: "100%",
                        objectFit: "cover",
                        border: "2px solid transparent",
                        overFlow: "hidden",
                        borderRadius: "1rem",
                      }}
                    />
                  )}
                  {backGroundImage !== userData.backgroundImageUrl ? (
                    <div
                      onClick={handleCancelUploadBackGroundImage}
                      style={deleteImageIcon}
                    >
                      <IoRemoveCircleOutline
                        color="rgb(0, 119, 234)"
                        size={"2rem"}
                      />
                    </div>
                  ) : (
                    <div
                      onClick={handleBackGroundImageClick}
                      style={clickToAddImage}
                    >
                      <IoCameraReverseOutline
                        color="rgb(0, 119, 234)"
                        size={"2rem"}
                      />
                    </div>
                  )}
                </div>

                <div className="flex profilePage_ProfileImage">
                  <div>
                    <div className="profilePage_ProfileImage_Img_Container">
                      {/* <img src={userBaseUrl + userData?.profileImageUrl} className=" profilePage_ProfileImage_Img" alt="b" /> */}

                      <input
                        type="file"
                        accept="image/*"
                        onChange={handleProfileImageChange}
                        style={{ display: "none" }}
                        ref={fileInputRef_ProfileImage}
                      />
                      {profileImagePreview ? (
                        <div className="profilePage_ProfileImage_Img">
                          <img
                            src={profileImagePreview}
                            alt=""
                            style={{
                              width: "100%",
                              height: "100%",
                              objectFit: "cover",
                              border: "2px solid transparent",
                              overFlow: "hidden",
                              borderRadius: "1rem",
                            }}
                          />
                        </div>
                      ) : (
                        <div className="profilePage_ProfileImage_Img">
                          <img
                            src={userBaseUrl + profileImage}
                            alt="Upload Icon"
                            onClick={handleProfileImageClick}
                            style={{
                              width: "100%",
                              height: "100%",
                              objectFit: "cover",
                              border: "2px solid transparent",
                              overFlow: "hidden",
                              borderRadius: "1rem",
                            }}
                          />
                        </div>
                      )}
                      {profileImage !== userData.profileImageUrl ? (
                        <div
                          onClick={handleCancelUploadProfileImage}
                          style={deleteImageIcon}
                        >
                          <IoRemoveCircleOutline
                            color="rgb(0, 119, 234)"
                            size={"2rem"}
                          />
                        </div>
                      ) : (
                        <div
                          onClick={handleProfileImageClick}
                          style={clickToAddImage}
                        >
                          <IoCameraReverseOutline
                            color="rgb(0, 119, 234)"
                            size={"2rem"}
                          />
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
              <div className="flex editprofilePage_BioAndContactDetails">
                <div className="flex editprofilePage_BioTitleUserName" style={dark ? { backgroundColor: "#011a32" } : {}}>
                  <div className="flex profilePage_UserName">
                    <span className="profilePage_UserName">
                      <UserNameInputComponent
                        userName={userName}
                        setUserName={setUserName}
                        isDarkMode={isDarkMode}
                      />
                    </span>
                  </div>
                  <div className="flex profilePage_Title">
                    <span className="profilePage_Title">
                      <UserTitleInputComponent
                        userTitle={userTitle}
                        setUserTitle={setUserTitle}
                        isDarkMode={isDarkMode}
                      />
                    </span>
                  </div>
                  <div className="flex profilePage_Bio">
                    <ReactQuill
                      className="custom-quill"
                      value={userBio}
                      onChange={(value) => { if (value.replace(/<[^>]+>/g, "").length <= 700) setUserBio(value); }}
                      placeholder="Tell us about yourself... (max 700 characters)"
                      modules={{
                        toolbar: false,
                      }}
                      style={{
                        color: dark ? "rgba(255,255,255,0.9)" : parrotTextDarkBlue,
                        fontFamily: "Nunito, sans-serif",
                        fontWeight: "600",
                        fontSize: "1.2rem",
                        borderRadius: "1.5rem",
                        padding: "0rem",
                        backgroundColor: dark ? "#0a2745" : "#007bff21",
                        width: "100%",
                      }}
                    />
                    <style>
                      {`
                            .ql-container {
                              border: none !important;
                            }
                            .ql-editor {
                              border: none !important;
                              padding: 0.5rem !important;
                              padding-left: 1rem !important;
                              padding-top: 1rem !important;
                              font-family: "Nunito", sans-serif !important;
                              font-weight: 600;
                              font-size: 1.2rem;
                              line-height: 1.65rem;
                              letter-spacing: 0.015em;
                            }
                            .custom-quill .ql-editor.ql-blank::before {
                              // color: red;
                              font-size: 1rem;
                              font-style: normal;
                              opacity: 0.6;
                            }
                            .custom-quill .ql-editor::-webkit-scrollbar {
                              width: 6px;
                            }
                            .custom-quill .ql-editor::-webkit-scrollbar-track {
                              background: ${dark ? "#0a2745" : "#007bff21"};
                              border-radius: 3px;
                            }
                            .custom-quill .ql-editor::-webkit-scrollbar-thumb {
                              background-color: ${dark ? "rgba(255,255,255,0.2)" : "rgba(0,123,255,0.3)"};
                              border-radius: 3px;
                            }
                            .custom-quill .ql-editor::-webkit-scrollbar-thumb:hover {
                              background-color: ${dark ? "rgba(255,255,255,0.35)" : "rgba(0,123,255,0.5)"};
                            }
                          `}
                    </style>
                  </div>
                </div>
                {/* <div className="flex editProfilePage_ContactDetails" style={{ backgroundColor: " " }}>
                        {isSuccessUser ?
                          <EditProfileSocialsComponent userData={userData} />
                          : null
                        }
                      </div> */}
              </div>
            </div>

            <div className="flex flex-col profilePage_BottomRight">
              <div
                className="flex flex-col"
                style={{
                  paddingLeft: ".5rem",
                  borderRadius: "1.5rem",
                  height: "calc(100vh - 3.5rem)",
                  width: "calc(100% - 0%)",
                  flexDirection: "column",
                }}
              >
                <div
                  style={{
                    // backgroundColor: dark ? "rgba(10,34,64,0.3)" : "rgba(255, 255, 255, 0.08)",
                    borderRadius: "1.5rem",
                    height: "100%",
                    flexDirection: "column",
                    width: "100%",
                    position: "relative",
                  }}
                >
                  {isSuccessUser ? (
                    <EditProfileSocialsComponent
                      isDarkMode={isDarkMode}
                      userData={userData}
                      setEmail={setEmail}
                      setDisplayEmail={setDisplayEmail}
                      setPhoneNumber={setPhoneNumber}
                      setFacebookProfile={setFacebookProfile}
                      setInstagramProfile={setInstagramProfile}
                      setTwitterProfile={setTwitterProfile}
                      setTiktokProfile={setTiktokProfile}
                      setLinkedinProfile={setLinkedinProfile}
                      setYoutubeProfile={setYoutubeProfile}
                      setEmailHidden={setEmailHidden}
                      email={email}
                      displayEmail={displayEmail}
                      phoneNumber={phoneNumber}
                      facebookProfile={facebookProfile}
                      instagramProfile={instagramProfile}
                      twitterProfile={twitterProfile}
                      tiktokProfile={tiktokProfile}
                      linkedinProfile={linkedinProfile}
                      youtubeProfile={youtubeProfile}
                      emailHidden={emailHidden}
                    />
                  ) : null}

                  <div style={{
                    display: "flex",
                    flexDirection: "row",
                    position: "absolute",
                    width: "20rem",
                    height: "8rem",
                    bottom: "0",
                    left: "50%",
                    transform: "translateX(-50%)",
                  }}>
                    <input
                      type="text"
                      value={honeyPotValue}
                      onChange={(e) => setHoneyPotValue(e.target.value)}
                      style={{ display: "none" }}
                      autoComplete="off"
                    />
                    <span
                      onClick={() => handleUpdateChanges()}
                      style={UpdateChangesButton}
                    >
                      {isUpdatingProfile ? <UpdateProfileSpinner /> : "Save Changes"}
                    </span>

                    <span
                      onClick={() => setShowDeleteModal(true)}
                      style={DeleteAccountButton}
                    >
                      Delete Account
                    </span>

                  </div>



                  {showDeleteModal && (
                    <div style={deleteModalOverlayStyle}>
                      <div style={deleteModalStyle}>
                        <div style={{ fontSize: "1.2rem", fontWeight: 800, color: "#163A5F", marginBottom: "0.5rem" }}>Delete Account</div>
                        <div style={{ fontSize: "1.1rem", color: "#163A5F", marginBottom: "1.5rem", lineHeight: 1.5, textAlign: "center" }}>
                          Your account will be deactivated. If you are a host with active voyages, your trip details will remain visible to your counterparties. As mentioned in the Terms of Use, Parrots may contact you via your registered email in the event of urgent coordination, and prompt responsiveness to guests is required for active trips and ongoing commitments.
                        </div>
                        <div style={{ display: "flex", gap: "0.75rem" }}>
                          <button onClick={() => setShowDeleteModal(false)} style={deleteCancelBtnStyle}>Cancel</button>
                          <button onClick={handleDeleteAccount} disabled={isDeletingAccount} style={deleteConfirmBtnStyle}>
                            {isDeletingAccount ? "Deleting…" : "Yes, Delete My Account"}
                          </button>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </header>
    </div>
  ) : null;
}

const UpdateProfileSpinner = () => {
  return (
    <div
      style={{
        backgroundColor: "rgba(0, 119, 234,0.1)",
        borderRadius: "1.5rem",
        position: "relative",
        margin: "auto",
        height: "2.2rem",
        display: "flex", // Added for vertical alignment
        alignItems: "center", // Center vertically
      }}
    >
      <div
        className="spinner"
        style={{
          height: "1rem",
          width: "1rem",
          border: "3px solid white",
          borderTop: "3px solid #1e90ff",
        }}
      ></div>
    </div>
  );
};

const deleteImageIcon = {
  // backgroundColor: " #3c9dee",
  backgroundColor: "white",
  position: "absolute",
  bottom: ".5rem",
  right: "0.5rem",
  borderRadius: "4rem",
  alignContent: "center",
  justifyItems: "center",
  lineHeight: "1.5rem",
  padding: "1rem",
  cursor: "pointer",
  transition: "transform 0.3s ease-in-out",
};

const clickToAddImage = {
  backgroundColor: "white",
  position: "absolute",
  bottom: ".5rem",
  right: "0.5rem",
  borderRadius: "4rem",
  alignContent: "center",
  justifyItems: "center",
  lineHeight: "1.5rem",
  padding: "1rem",
};

const UpdateChangesButton = {
  position: "absolute",
  fontSize: "1.4rem",
  fontWeight: 800,
  color: "white",
  borderRadius: "1.5rem",
  paddingRight: "2rem",
  paddingLeft: "2rem",
  marginTop: "0.3rem",
  backgroundColor: "#007bff",
  cursor: "pointer",
  border: "none",
  boxShadow:
    "0 4px 6px rgba(0, 0, 0, 0.3), inset 0 -4px 6px rgba(0, 0, 0, 0.3)",
  padding: "0.2rem",
  width: "20rem",
};


const DeleteAccountButton = {
  position: "absolute",
  fontSize: "1.4rem",
  fontWeight: 800,
  color: "white",
  borderRadius: "1.5rem",
  paddingRight: "2rem",
  paddingLeft: "2rem",
  marginTop: "3.5rem",
  backgroundColor: parrotRed,
  cursor: "pointer",
  border: "none",
  boxShadow:
    "0 4px 6px rgba(0, 0, 0, 0.3), inset 0 -4px 6px rgba(0, 0, 0, 0.3)",
  padding: "0.2rem",
  width: "20rem",
};

const buttonsContainer = {
  position: "absolute",
  top: "0",
  left: "0",
  width: "100%",
  display: "flex",
  justifyContent: "flex-end", // single child aligned right
  alignItems: "center",
  padding: "0.5rem",
  // backgroundColor: "rgba(255, 255, 255, 0.54321) ",
  boxSizing: "border-box",
  borderTopLeftRadius: "1rem",
  borderTopRightRadius: "1rem",
}

const navigationButton = {
  borderRadius: "1.5rem",
  backgroundColor: "white",
  color: "#007bff",
  padding: "0.2rem 0.8rem",
  textAlign: "center",
  fontWeight: "bold",
  cursor: "pointer",
  fontSize: "1.1rem",
  border: "none",
  boxShadow: "0 4px 6px rgba(0,0,0,0.3), inset 0 -4px 6px rgba(0,0,0,0.3)",
  transition: "box-shadow 0.2s ease",
  WebkitFontSmoothing: "antialiased",
  MozOsxFontSmoothing: "grayscale",
};

const deleteAccountButtonStyle = {
  fontSize: "1.4rem",
  fontWeight: 800,
  color: "white",
  borderRadius: "1.5rem",
  paddingRight: "2rem",
  paddingLeft: "2rem",
  marginTop: "0.3rem",
  backgroundColor: "#cb0404",
  cursor: "pointer",
  border: "none",
  boxShadow: "0 4px 6px rgba(0, 0, 0, 0.3), inset 0 -4px 6px rgba(0, 0, 0, 0.3)",
  padding: "0.2rem",
  width: "20rem",
};

const deleteModalOverlayStyle = {
  position: "fixed",
  top: 0, left: 0, right: 0, bottom: 0,
  backgroundColor: "rgba(0,0,0,0.5)",
  zIndex: 1000,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
};

const deleteModalStyle = {
  backgroundColor: "#fff",
  borderRadius: "1.5rem",
  padding: "2rem",
  width: "40rem",
  boxShadow: "0 8px 30px rgba(0,0,0,0.2)",
};

const deleteCancelBtnStyle = {
  flex: 1,
  padding: "0.75rem",
  borderRadius: "2rem",
  border: "none",
  backgroundColor: "#f2f4f7",
  color: "#9aa0aa",
  fontWeight: 700,
  fontSize: "0.95rem",
  cursor: "pointer",
};

const deleteConfirmBtnStyle = {
  flex: 1,
  padding: "0.75rem",
  borderRadius: "2rem",
  border: "none",
  backgroundColor: "#cb0404",
  color: "white",
  fontWeight: 700,
  fontSize: "0.95rem",
  cursor: "pointer",
};